import React, {useState} from "react";
import Typography from "@mui/material/Typography"
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import classnames from "classnames";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import _ from "lodash";
import {useCalcContext} from "../../contexts/CalcContext";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import {CostCalculator} from "../../components/CostCalculator";
import {costUpdate} from "../../components/costCalculatorMath";
import Switch from "@mui/material/Switch";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";

// Currency field matching the redesign spec: a bordered `.pricing-input-wrap`
// holds the "$" prefix span and the input together, with the input's MUI
// chrome stripped (InputProps={{}} drops MUI's built-in start-adornment).
const PricingCurrencyInput = ({ className, showPrefix = true, ...props }) => (
    <div className={classnames('pricing-input-wrap', className)}>
        {showPrefix && <span className={'currency-prefix'}>$</span>}
        <CustomCurrencyInput InputProps={{}} className={''} {...props} />
    </div>
)

export const CalcPagePricing = ({ deskDeal, createNewPurchase, getRebates, isCreatingCash }) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const [isCreatingLease, setIsCreatingLease] = useState(false)
    const [isCreatingPurchase, setIsCreatingPurchase] = useState(false)

    const {
        calcState,
        setCalcState,
        carState,
        isUsed
    } = useCalcContext()

    const isLoading = (isCreatingPurchase || isCreatingLease)

    const isInputDisabled = !carState.year || !carState.make || !carState.model || !carState.trim
    const isDisabled = !calcState.msrp || !calcState.cost || !calcState.base_car_id

    const sellingPrice =  _.round(Number(calcState.cost) + Number(calcState.profit), 0)

    // A hand-typed Cost is canonical like any other: it re-derives the
    // calculator's MSRP/Invoice relationships in the same state write. This
    // replaces an onKeyDown that cleared those columns in a separate write —
    // whether the clear survived depended on React flushing the keydown before
    // the input event, and if it lost, the old offsets snapped Cost back.
    const setCost = (value) => setCalcState({
        ...calcState,
        ...costUpdate(calcState.msrp, calcState.invoice, value),
    })

    return (
        <Box className={classnames("CalcPageInfo_box", {
            mobile: isTabletOrMobile
        })}>
            <Stack
                direction="column"
                justifyContent="space-between"
                style={{
                    height: '100%'
                }}
            >
                <div>
                    <Typography
                        variant="h6"
                        component="div"
                        className={'header'}
                    >
                        <LocalOfferOutlinedIcon color="primary"/>
                        <span>
                            Vehicle Pricing
                        </span>
                    </Typography>

                    {
                        !isUsed &&
                        <React.Fragment>

                            <div
                                className={'payment-row'}
                            >
                            <span
                                className={'payment-row_name'}
                            >
                                MSRP
                            </span>
                                <PricingCurrencyInput
                                    className={classnames({
                                        loading: isLoading
                                    })}
                                    placeholder={'0'}
                                    value={calcState.msrp}
                                    onChange={(value) => setCalcState({...calcState, msrp: value})}
                                    allowNull={true}
                                    disabled={isInputDisabled}
                                />
                            </div>
                            <div
                                className={'payment-row'}
                            >
                            <span
                                className={'payment-row_name'}
                            >
                                Invoice
                            </span>
                                <PricingCurrencyInput
                                    id="calc-invoice-input"
                                    className={classnames({
                                        loading: isLoading
                                    })}
                                    value={calcState.invoice}
                                    onChange={(value) => setCalcState({...calcState, invoice: value})}
                                    disabled={isInputDisabled}
                                />
                            </div>
                        </React.Fragment>
                    }
                    {
                        isUsed &&
                        <React.Fragment>
                            <div
                                className={'payment-row'}
                            >
                            <span
                                className={'payment-row_name'}
                            >
                                Book Value:
                            </span>
                                <PricingCurrencyInput
                                    className={classnames({
                                        loading: isLoading
                                    })}
                                    value={calcState.msrp}
                                    onChange={(value) => {
                                        setCalcState({...calcState, invoice: value, msrp: value})
                                    }}
                                    disabled={isInputDisabled}
                                />
                            </div>
                            <div
                                className={'payment-row'}
                            >
                                <span
                                    className={'payment-row_name'}
                                >
                                    Certified Pre-Owned: <strong>{calcState.is_certified_preowned ? 'Yes' : 'No'}</strong>
                                </span>

                                <Switch
                                    size={'small'}
                                    checked={calcState.is_certified_preowned}
                                    onChange={(value) => setCalcState({
                                        ...calcState,
                                        is_certified_preowned: !calcState.is_certified_preowned
                                    })}
                                />
                            </div>
                            <div
                                className={'payment-row'}
                            >
                                <span
                                    className={'payment-row_name'}
                                >
                                    Current Mileage:
                                </span>
                                <PricingCurrencyInput
                                    className={classnames({
                                        loading: isLoading
                                    })}
                                    showPrefix={false}
                                    value={calcState.current_mileage}
                                    onChange={(value) => setCalcState({...calcState, current_mileage: value})}
                                    disabled={isInputDisabled}
                                />
                            </div>
                        </React.Fragment>
                    }
                    <div
                        className={'payment-row'}
                    >
                            <span
                                className={'payment-row_name'}
                            >
                                Cost <CostCalculator state={calcState} setState={setCalcState} isLoading={isLoading}/>
                            </span>
                        <PricingCurrencyInput
                            className={classnames({
                                loading: isLoading
                            })}
                            placeholder={'0'}
                            value={calcState.cost}
                            onChange={setCost}
                            allowNull={true}
                            disabled={isInputDisabled}
                    />
                </div>
                    <div
                            className={'payment-row profit-row'}
                        >
                            <span
                                className={'payment-row_name'}
                            >
                                Profit
                            </span>
                        <PricingCurrencyInput
                            className={classnames({
                                loading: isLoading
                            })}
                            decimalScale={0}
                            placeholder={calcState.profit}
                            value={calcState.profit}
                            onChange={(value) => setCalcState({...calcState, profit: value})}
                            disabled={isInputDisabled}
                        />
                    </div>
                    <div
                        className={'payment-row'}
                    >
                            <span
                                className={'payment-row_name'}
                            >
                                Selling Price
                            </span>
                        <PricingCurrencyInput
                            className={classnames('disabled', {
                                loading: isLoading
                            })}
                            decimalScale={0}
                            value={sellingPrice}
                            disabled={true}
                        />
                    </div>
                </div>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Tooltip title="Press Enter to Calculate" arrow placement={'bottom'}>
                            <span>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    className={classnames('dealPage-button action-btn-lease', {
                                        isDisabled: isDisabled
                                    })}
                                    onClick={() => deskDeal(false, false)}
                                    disabled={isDisabled}
                                >
                                    {
                                        isCreatingLease ? 'Calculating...' : 'Lease'
                                    }
                                </Button>
                            </span>
                    </Tooltip>
                    <span>
                            <Button
                                variant="outlined"
                                disableElevation
                                className={classnames('dealPage-button action-btn-finance', {
                                    isDisabled: isDisabled
                                })}
                                onClick={() => deskDeal(true, false)}
                                disabled={isDisabled}
                            >
                                {
                                    isCreatingPurchase ? 'Calculating...' : 'Finance'
                                }
                            </Button>
                        </span>
                        <span>
                            <Button
                                variant="outlined"
                                disableElevation
                                className={classnames('dealPage-button action-btn-cash', {
                                    isDisabled: isDisabled
                                })}
                                onClick={() => deskDeal(true, true)}
                                disabled={isCreatingCash}
                            >
                                {
                                    isCreatingCash ? 'Calculating...' : 'Cash'
                                }
                            </Button>
                        </span>
                </Stack>
            </Stack>
        </Box>
    )
}